import { FC, Fragment } from 'react'

import { Box, Icon, Table, Text, colors } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import { CustomTooltip } from 'modules/finance/components/FinancialWorkspace/components/CustomTooltip'
import {
  PaginatedTableItem,
  TableItemData,
} from 'modules/finance/components/FinancialWorkspace/types/table'

import { SkeletonValue } from '../../../../SkeletonValue'
import { dashedRowProps, stickyColumnProps } from '../../../const'
import { Delta } from '../../Delta'
import { Cell } from '../Cell'
import { LoadingRow } from '../LoadingRow'
import { STICKY_CELL_WIDTH, WEIGHT } from '../const'

const PADDING_LEFT: Record<number, string> = {
  0: '20px',
  1: '40px',
  2: '60px',
}

interface Props {
  level: number
  isOpen: boolean
  isLastRow: boolean
  showBlank?: boolean
  headerCount: number
  toggleRow: () => void
  item: PaginatedTableItem
}

export const CollapsableRow: FC<Props> = ({
  level,
  item,
  isOpen,
  toggleRow,
  showBlank,
  headerCount,
}) => {
  const { hasComparisonDate } = useFinancialWorkspace()

  const isItalic = item.isSummarized || item.isUngrouped || item.italic

  const isGrey = item.isSummarized || item.isUngrouped || item.grey

  const renderCell = (cell?: TableItemData, sticky?: boolean, loading?: boolean) => (
    <Table.Cell wide sticky={sticky ? stickyColumnProps : undefined}>
      <Box flexDirection="row" alignItems="center" justifyContent="flexEnd" gap="s8">
        {loading ? (
          <SkeletonValue />
        ) : (
          <Cell cell={cell} showBlank={showBlank} level={level} italic={isItalic} />
        )}
      </Box>
    </Table.Cell>
  )

  if (item.loading) {
    return <LoadingRow level={level} />
  }

  return (
    <Table.Row
      style={{
        top: 0,
        height: '56px',
        position: 'sticky',
        backgroundColor: isGrey ? colors.grey100 : undefined,
        ...(item.dashed ? dashedRowProps : {}),
      }}
    >
      {item.label && (
        <Table.Cell
          wide
          paddingY="s0"
          sticky={stickyColumnProps}
          style={{
            width: STICKY_CELL_WIDTH,
            minWidth: STICKY_CELL_WIDTH,
            paddingLeft: PADDING_LEFT[level],
            background: isGrey ? colors.grey100 : 'grey200',
            ...(item.dashed ? dashedRowProps : {}),
          }}
        >
          <Box
            gap="s8"
            width="full"
            height="full"
            paddingY="s16"
            paddingRight="s24"
            flexDirection="row"
            onClick={toggleRow}
            alignItems="center"
          >
            <Box borderRadius={{ all: 'abs004' }} justifyContent="center">
              <Icon.Small icon={isOpen ? 'chevron-down' : 'chevron-right'} color="grey800" />
            </Box>
            <Text.Normal
              ellipsis
              size="200"
              color="grey1000"
              weight={WEIGHT[level]}
              fontStyle={isItalic ? 'italic' : 'normal'}
            >
              <Box flexDirection="row" alignItems="center" gap="s4">
                <Box flexDirection="column">
                  <Box flexDirection="row">{item.label}&nbsp;</Box>
                  {item.subLabel ? (
                    <Text.Normal
                      ellipsis
                      size="200"
                      color="grey1000"
                      weight={WEIGHT[level + 1]}
                      fontStyle={isItalic ? 'italic' : 'normal'}
                    >
                      {item.subLabel}
                    </Text.Normal>
                  ) : null}
                </Box>
                {item.iconLabel && <Icon.Medium icon={item.iconLabel} color="grey800" />}
                {item.tooltip && (
                  <CustomTooltip label={item.tooltip}>
                    <Icon.Medium icon="info-circle-line" color="grey800" />
                  </CustomTooltip>
                )}
                {item.isSummarized && (
                  <CustomTooltip label={i18n.t('finance.financial_workspace.excluded_concepts')}>
                    <Icon.Medium icon="info-circle-line" color="grey800" />
                  </CustomTooltip>
                )}
              </Box>
            </Text.Normal>
          </Box>
        </Table.Cell>
      )}

      {[...Array(headerCount)].map((_, index) => {
        const cell = item.data[index]
        const compareToData = item.comparisonData?.[index]
        const sticky = index === 0 && !item.label
        const withBorder = index != headerCount - 1
        return (
          <Fragment key={index}>
            {hasComparisonDate && renderCell(compareToData, sticky, item.loading)}
            {renderCell(cell, sticky, item.loading)}
            {hasComparisonDate ? (
              <Table.Cell
                wide
                paddingY="s0"
                style={{ borderRight: withBorder ? '1px solid #E2E2E5' : undefined }}
              >
                <Box flexDirection="row" alignItems="center" justifyContent="flexEnd" gap="s8">
                  <Delta
                    noDiffResult="-"
                    secondValue={Number(cell?.totalAmountInCents)}
                    firstValue={Number(compareToData?.totalAmountInCents)}
                  />
                </Box>
              </Table.Cell>
            ) : null}
          </Fragment>
        )
      })}
    </Table.Row>
  )
}
